<script setup lang="ts">
import { object, string } from 'yup'
import type { InferType } from 'yup'
import type { FormSubmitEvent } from '@nuxt/ui'
const { t } = useI18n()

const toast = useToast()
const config = useRuntimeConfig()
const schema = object({
    email: string().email(t("partials.shopNewsletter.emailInvalid")).required(t("partials.shopNewsletter.emailRequired")),
})

type Schema = InferType<typeof schema>

const state = reactive({
    email: undefined,
})

async function onNewsletterSubscription(event: FormSubmitEvent<Schema>) {
    try {
        const email = event.data.email?.trim()
        if (!email) {
            toast.add({
                title: t("partials.shopNewsletter.emailInvalid"),
                color: 'error',
            })
            return
        }

        const body: Record<string, any> = { email }
        const groupId = config.public.mailerLiteNewGuidesGroupID
        if (groupId) body.groups = [groupId]

        const res = await $fetch('https://connect.mailerlite.com/api/subscribers', {
            method: 'POST',
            headers: {
                Authorization: `Bearer ${config.public.mailerLiteAPI}`,
                'Content-Type': 'application/json',
            },
            body,
        })

        // MailerLite typically returns { data: { status: 'active', ... } }
        const status = (res as any)?.data?.status
        if (status === 'active') {
            toast.add({
                title: t("partials.shopNewsletter.successful"),
                color: 'success',
            })
            state.email = undefined
        } else {
            toast.add({
                title: t("partials.shopNewsletter.error"),
                color: 'error',
            })
        }
    } catch (error: any) {
        const apiMsg =
            error?.data?.error?.message ||
            error?.data?.message ||
            t("partials.shopNewsletter.error")
        toast.add({
            title: apiMsg,
            color: 'error',
        })
    }
}
</script>

<template>
    <div class="flex flex-col justify-center items-center w-full bg-[#DCDCF7] rounded-[65px] py-8 px-8 lg:py-12 lg:px-16 z-1">
        <div class="flex flex-col gap-[8px] mb-6">
            <h2 class="text-center uppercase text-[20px] lg:text-[28px] text-secondary">{{ t("partials.shopNewsletter.title") }}</h2>
            <p class="text-center text-secondary">{{ t("partials.shopNewsletter.description") }}</p>
        </div>
        <UForm :schema="schema" :state="state" @submit="onNewsletterSubscription" class="flex flex-col lg:flex-row gap-4.5 w-full items-center justify-center mb-4">
            <UFormField label="Email" class="w-full max-w-[500px]">
                <UInput v-model="state.email" :placeholder="t('partials.shopNewsletter.placeholder')" class="newsletter-email-input w-full" />
            </UFormField>
            <UButton type="submit" color="secondary" variant="outline" class="cursor-pointer">{{ t("partials.shopNewsletter.button") }}</UButton>
        </UForm>
        <p class="text-center text-[12px] lg:text-[14px] text-secondary">{{ t("partials.shopNewsletter.notice") }}</p>
    </div>
</template>

<style scoped>
@media (max-width: 1023px) {
    .newsletter-email-input input {
        text-align: center !important;
    }
}
</style>
